import { DEFAULT_RADIUS, boxCorners, centreOf, declaredStates, lessonBounds, objectBounds, unionBounds, } from './bounds.js';
import { boxIntersects, sphereIntersects } from './intersect.js';
import { LECTERN, NAVIGATION_ASSEMBLY, lecternAssembly, lecternHeight, } from './lectern.js';
import { CENTRED, FRAMING, LECTERN_EYES, anglesOf, awayFrom, boxSamples, centreOfBounds, clearLine, grown, hull, inside, insideBox, lecternPose, overlaps, panelOutline, roundBounds, spanOf, toRig, viewPlacement, yawTowards, } from './lectern-view.js';
import { POSTER_LIMITS, isPosterKind, posterNormal } from './posters.js';
import { MODEL_DISTANCE, modelPosesAt, modelScale, modelSlugOf, replayModels, } from './models.js';
import { shortestTurn } from './model-motion.js';
import { footprintDistance, isSmallModel, isStaticModel, modelFootprint, } from './model-distance.js';
import { objectSolids, solidTouches } from './solids.js';
import { sceneStateAt } from './scene-state.js';
import { isStagedLesson } from './panos.js';
import { featureOf } from './world-data.js';
import { featuresOf, stageOf } from './world-catalogue.js';
/**
 * Framing a step's subject, and the lectern that steps aside when it must
 * (docs/superpowers/specs/2026-09-28-clear-view-design.md).
 *
 * Frame first, then slide aside: for a step about an object the pupil is
 * placed so the subject fills a sensible part of the view and sits above the
 * sight line over the lectern's top edge; only when that cannot be done does
 * the lectern swing along its arc. Pure, so the headset places the pupil and
 * the API's generator check judges a lesson with the same numbers.
 *
 * Angles are degrees; azimuth is positive to the right, elevation positive
 * up. Positions are the room's metres.
 */
const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;
/**
 * How high the lectern stands for eyes at `eye` metres above the floor, on a
 * step without a question (`lecternHeight` for any other).
 */
export function lecternHeightFor(eye) {
    return lecternHeight(eye);
}
export { LECTERN };
// The lectern's and a subject's angles, split out for the world kit's copy (lectern-view.ts, ADR-091).
export { CENTRED, FRAMING, LECTERN_EYES, anglesOf, clearLine, grown, inside, lecternPose, panelOutline, roundBounds, spanOf, toRig, viewPlacement, yawTowards, };
// --- measuring a subject from the eyes ---------------------------------------
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const length = (v) => Math.hypot(v[0], v[1], v[2]);
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
/** Half the angle something `half` metres across subtends at `distance`; 90 when the eye is in it. */
const halfAngle = (half, distance) => distance <= half ? 90 : Math.asin(half / distance) * DEG;
/** How far the eyes are from the nearest surface of an object; 0 inside it. */
export function surfaceDistance(bounds, eye) {
    if (bounds.ring) {
        // Across the ring on the ground plan, and above or below its heights.
        const { centre, inner, outer } = bounds.ring;
        const d = Math.hypot(eye[0] - centre[0], eye[2] - centre[1]);
        const { min, max } = bounds.obstacle;
        return Math.hypot(Math.max(inner - d, 0, d - outer), Math.max(min[1] - eye[1], 0, eye[1] - max[1]));
    }
    if (bounds.round) {
        const r = Math.max(bounds.round.radius, bounds.round.height);
        return Math.max(0, length(sub(eye, bounds.round.centre)) - r);
    }
    const { min, max } = bounds.obstacle;
    const gap = (i) => Math.max(min[i] - eye[i], 0, eye[i] - max[i]);
    return Math.hypot(gap(0), gap(1), gap(2));
}
/**
 * How much of the view a subject fills, turned to face its centre: half the
 * angle it spans in height and in width.
 */
export function fillOf(bounds, eye) {
    const centre = centreOfBounds(bounds);
    const toCentre = sub(centre, eye);
    const distance = length(toCentre);
    if (bounds.round) {
        return {
            tall: halfAngle(bounds.round.height, distance),
            wide: halfAngle(bounds.round.radius, distance),
        };
    }
    if (insideBox(bounds, eye) || distance < 1e-9)
        return { tall: 90, wide: 90 };
    const f = [toCentre[0] / distance, toCentre[1] / distance, toCentre[2] / distance];
    // Right is forward × up; straight up or down, any right will do.
    const side = Math.hypot(f[0], f[2]);
    const r = side < 1e-9 ? [1, 0, 0] : [-f[2] / side, 0, f[0] / side];
    const u = [r[1] * f[2] - r[2] * f[1], r[2] * f[0] - r[0] * f[2], r[0] * f[1] - r[1] * f[0]];
    let azMin = Infinity;
    let azMax = -Infinity;
    let elMin = Infinity;
    let elMax = -Infinity;
    for (const point of boxSamples(bounds.box)) {
        const v = sub(point, eye);
        const x = dot(v, r);
        const y = dot(v, u);
        const z = dot(v, f);
        if (z <= 1e-9)
            return { tall: 90, wide: 90 };
        const az = Math.atan2(x, z) * DEG;
        const el = Math.atan2(y, Math.hypot(x, z)) * DEG;
        azMin = Math.min(azMin, az);
        azMax = Math.max(azMax, az);
        elMin = Math.min(elMin, el);
        elMax = Math.max(elMax, el);
    }
    return { tall: (elMax - elMin) / 2, wide: (azMax - azMin) / 2 };
}
const keptAway = (keep, eye) => (keep ?? []).every(({ footprint, distance }) => footprintDistance(footprint, [eye[0], eye[2]]) >= distance - 1e-9);
function place(input, direction) {
    const { subject, eye } = input;
    const centre = centreOfBounds(subject);
    const line = clearLine(eye, input.assembly);
    const eyeAt = (d) => [
        centre[0] - direction[0] * d,
        eye,
        centre[2] - direction[1] * d,
    ];
    const yawAt = (p) => Math.atan2(-(centre[0] - p[0]), -(centre[2] - p[2]));
    const passes = (d, rules) => {
        const p = eyeAt(d);
        for (const rule of rules) {
            if (rule === 'surface' && surfaceDistance(subject, p) < (input.surface ?? FRAMING.surface)) {
                return false;
            }
            if (rule === 'keep' && !keptAway(input.keepAway, p))
                return false;
            if (rule === 'fill') {
                const fill = fillOf(subject, p);
                if (fill.tall > FRAMING.tallHalf || fill.wide > FRAMING.wideHalf)
                    return false;
            }
            if (rule === 'elevation' || rule === 'clear') {
                const span = spanOf(subject, p, yawAt(p));
                if (rule === 'clear' && span.elMin < line)
                    return false;
                if (rule === 'elevation' &&
                    (Math.abs(span.centreEl) > FRAMING.centreElevation || span.elMax > FRAMING.topElevation)) {
                    return false;
                }
            }
        }
        return true;
    };
    const start = Math.max(0, input.keepOut ?? 0);
    /** The nearest distance, in 5 cm steps and then to the millimetre, that keeps every rule. */
    const nearest = (rules) => {
        let previous;
        for (let d = start; d <= FRAMING.maxDistance + 1e-9; d += FRAMING.scanStep) {
            if (!passes(d, rules)) {
                previous = d;
                continue;
            }
            if (previous === undefined)
                return d;
            let lo = previous;
            let hi = d;
            while (hi - lo > 0.0005) {
                const mid = (lo + hi) / 2;
                if (passes(mid, rules))
                    hi = mid;
                else
                    lo = mid;
            }
            return hi;
        }
        return undefined;
    };
    const fillAt = (d) => {
        const fill = fillOf(subject, eyeAt(d));
        return Math.max(fill.tall, fill.wide);
    };
    const everything = nearest(['surface', 'fill', 'elevation', 'clear', 'keep']);
    const butClear = nearest(['surface', 'fill', 'elevation', 'keep']);
    let d;
    if (everything !== undefined &&
        (butClear === undefined ||
            everything <= butClear + 1e-9 ||
            fillAt(everything) >= FRAMING.minFillHalf)) {
        d = everything;
    }
    else {
        // Only the clear line keeps it further back than a 16° fill: frame it
        // first, and let the lectern slide aside. Failing even that -- a subject
        // too big to fill less than 36° from anywhere -- as far back as the
        // search goes, where it is smallest.
        d =
            butClear ??
                nearest(['surface', 'fill', 'keep']) ??
                nearest(['surface', 'fill']) ??
                Math.max(start, FRAMING.maxDistance);
    }
    // On a world's stage: the farthest place along the approach that is still
    // on it, the larger root of |centre − direction·d| = R, never nearer than
    // the orbit's keep-out. A line that misses the stage keeps its place.
    // ADR-061 (`strictStand`): a place short of the stage on the subject's side
    // moves back to the smaller root, as near as the stage lets it be.
    let pulledIn = false;
    let pushedBack = false;
    let wanted = 0;
    const within = input.standWithin;
    const best = Math.hypot(eyeAt(d)[0], eyeAt(d)[2]);
    if (within !== undefined && best > within + 1e-9) {
        const along = centre[0] * direction[0] + centre[2] * direction[1];
        const disc = along * along - (centre[0] ** 2 + centre[2] ** 2) + within * within;
        if (disc >= 0) {
            const near = along - Math.sqrt(disc);
            const back = input.strictStand === true && d < near;
            d = Math.max(start, back ? near : Math.min(d, along + Math.sqrt(disc)));
            pulledIn = true;
            pushedBack = back;
            wanted = best;
        }
    }
    const p = eyeAt(d);
    const lookAt = centreOf(subject.box);
    return {
        floor: [p[0], 0, p[2]],
        eye: p,
        lookAt,
        distance: d,
        fill: fillAt(d),
        needsAside: !passes(d, ['clear']),
        ...(pulledIn
            ? {
                pulledIn: true,
                ...(pushedBack ? { pushedBack: true } : {}),
                wanted,
                fits: passes(d, ['surface', 'fill']),
            }
            : {}),
    };
}
/**
 * Where the pupil stands for a subject: the nearest place, coming from
 * `approach`, where it fills at most 36° tall and 44° wide, sits above the
 * clear line, is within the elevation limits and has no surface nearer than
 * 1 m. Turned about the subject if another object is in the way.
 */
export function frameView(input) {
    const first = place(input, input.approach);
    const free = (view) => input.obstacles.every((obstacle) => surfaceDistance(obstacle, view.eye) >= FRAMING.obstacle) &&
        keptAway(input.keepAway, view.eye);
    if (free(first))
        return first;
    const [ax, az] = input.approach;
    for (const turn of FRAMING.turns) {
        const c = Math.cos(turn * RAD);
        const s = Math.sin(turn * RAD);
        const turned = place(input, [ax * c - az * s, ax * s + az * c]);
        if (free(turned))
            return turned;
    }
    return first;
}
const reachOf = (placement, assembly) => ({
    distance: placement.distance,
    assembly,
});
/**
 * The lectern with its buttons, standing at `pose` in the rig, grown by its
 * 5 cm clearance, as a box in the room: what must not touch a model.
 */
export function lecternBoxAt(pose, rig, assembly = NAVIGATION_ASSEMBLY, margin = LECTERN.clearance) {
    const eyeHeight = rig.eyeHeight ?? rig.eye[1];
    // The lectern's own axes in the rig: turned about y, then tilted ('YXZ').
    const cy = Math.cos(pose.yaw);
    const sy = Math.sin(pose.yaw);
    const ct = Math.cos(pose.tilt);
    const st = Math.sin(pose.tilt);
    const local = [
        [cy, 0, -sy],
        [st * sy, ct, st * cy],
        [ct * sy, -st, ct * cy],
    ];
    // And the rig's in the room.
    const cr = Math.cos(rig.yaw);
    const sr = Math.sin(rig.yaw);
    const toRoom = (v) => [v[0] * cr + v[2] * sr, v[1], -v[0] * sr + v[2] * cr];
    const x = (assembly.left + assembly.right) / 2;
    const y = (assembly.top + assembly.bottom) / 2;
    const middle = toRoom([
        pose.position[0] + x * local[0][0] + y * local[1][0],
        pose.position[1] + x * local[0][1] + y * local[1][1],
        pose.position[2] + x * local[0][2] + y * local[1][2],
    ]);
    return {
        centre: [rig.eye[0] + middle[0], rig.eye[1] - eyeHeight + middle[1], rig.eye[2] + middle[2]],
        half: [
            (assembly.right - assembly.left) / 2 + margin,
            (assembly.top - assembly.bottom) / 2 + margin,
            margin,
        ],
        axes: [toRoom(local[0]), toRoom(local[1]), toRoom(local[2])],
    };
}
/** The lectern placed so for the pupil at `eye`, as a box in the room (`lecternBoxAt`). */
export function lecternBox(input) {
    const eyeHeight = input.eyeHeight ?? input.eye[1];
    const assembly = input.assembly ?? NAVIGATION_ASSEMBLY;
    const { placement } = input;
    const pose = lecternPose(eyeHeight, placement.side, placement.degrees, reachOf(placement, assembly));
    return lecternBoxAt(pose, { eye: input.eye, yaw: input.yaw, eyeHeight }, assembly, input.margin);
}
/** Samples along a slide: enough that no 5 cm-clear model fits between two of them. */
const SLIDE_SAMPLES = 24;
/**
 * Whether the lectern can slide from one pose to another -- straight, turning
 * as it goes, as the headset slides it (render/lectern.ts) -- without passing
 * through a model on the way. When it cannot, the headset puts it there at once.
 */
export function slideClear(input) {
    const { from, to, rig, obstacles } = input;
    if (obstacles.length === 0)
        return true;
    for (let k = 1; k < SLIDE_SAMPLES; k++) {
        const t = k / SLIDE_SAMPLES;
        const pose = {
            position: [0, 1, 2].map((i) => from.position[i] + (to.position[i] - from.position[i]) * t),
            yaw: from.yaw + (to.yaw - from.yaw) * t,
            tilt: to.tilt,
        };
        const box = lecternBoxAt(pose, rig, input.assembly);
        if (obstacles.some((obstacle) => lecternTouches(box, obstacle)))
            return false;
    }
    return true;
}
/**
 * Whether the lectern's box touches a model: as the solids it is made of when
 * they are known, a planet as its sphere, anything else as its whole box.
 */
export function lecternTouches(box, bounds) {
    if (bounds.ring)
        return ringTouches(box, bounds.ring, bounds.obstacle);
    if (bounds.solids) {
        return (boxIntersects(box, bounds.obstacle) && bounds.solids.some((solid) => solidTouches(solid, box)));
    }
    const round = bounds.round;
    if (round && round.radius === round.height) {
        return sphereIntersects(box, round.centre, round.radius);
    }
    return boxIntersects(box, bounds.obstacle);
}
/** The eight corners of a turned box. */
function orientedCorners(box) {
    const corners = [];
    for (const a of [-1, 1]) {
        for (const b of [-1, 1]) {
            for (const c of [-1, 1]) {
                const k = [a * box.half[0], b * box.half[1], c * box.half[2]];
                corners.push([0, 1, 2].map((i) => box.centre[i] +
                    k[0] * box.axes[0][i] +
                    k[1] * box.axes[1][i] +
                    k[2] * box.axes[2][i]));
            }
        }
    }
    return corners;
}
/** How far `p` is from the segment `a`–`b`, in the plane. */
function segmentDistance(p, a, b) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy || 1)));
    return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}
/**
 * Whether the lectern's box touches a ring a model goes round: at the ring's
 * heights, its shadow on the ground plan reaches the ring -- not wholly in its
 * free middle, and not wholly outside it.
 */
function ringTouches(box, ring, heights) {
    const corners = orientedCorners(box);
    const ys = corners.map((c) => c[1]);
    if (Math.max(...ys) < heights.min[1] || Math.min(...ys) > heights.max[1])
        return false;
    const shadow = hull(corners.map((c) => [c[0] - ring.centre[0], c[2] - ring.centre[1]]));
    if (shadow.every((p) => Math.hypot(p[0], p[1]) < ring.inner))
        return false;
    if (inside([0, 0], shadow))
        return true;
    let nearest = Infinity;
    for (let i = 0; i < shadow.length; i++) {
        nearest = Math.min(nearest, segmentDistance([0, 0], shadow[i], shadow[(i + 1) % shadow.length]));
    }
    return nearest <= ring.outer;
}
/**
 * Where the lectern goes, and it never goes through a model.
 *
 * First for the view, as before: centred, or swung away from the subject by
 * the smallest whole degree that clears it by 2°, at most 45° -- with
 * hysteresis: a subject grazing the lectern does not make it swing, and a
 * swung lectern stays where it is while that still clears the subject.
 *
 * If that place touches a model (with 5 cm to spare), it searches: centred,
 * then both sides a degree at a time to 45°, on its 1.9 m arc and then pulled
 * closer (1.6 m, 1.35 m). It takes the first place clear of every model that
 * also keeps the subject in view; failing that, the first clear of every model
 * whatever it hides (`overlapsAfter`). A collision is never chosen: with
 * nowhere free it stays where the view wanted it and says `collides`.
 */
export function panelPlacement(input) {
    const { eye, yaw, subject } = input;
    const current = input.current ?? CENTRED;
    const eyeHeight = input.eyeHeight ?? eye[1];
    const assembly = input.assembly ?? NAVIGATION_ASSEMBLY;
    const obstacles = input.obstacles ?? [];
    const span = subject ? spanOf(subject, eye, yaw) : undefined;
    const hits = (placement, margin) => span !== undefined &&
        overlaps(grown(span.outline, margin), panelOutline(eyeHeight, placement.side, placement.degrees, reachOf(placement, assembly)));
    const touches = (placement) => {
        if (obstacles.length === 0)
            return false;
        const box = lecternBox({ eye, yaw, placement, eyeHeight, assembly });
        return obstacles.some((obstacle) => lecternTouches(box, obstacle));
    };
    const centredFrom = (placement) => placement.side === 0 && placement.distance === undefined;
    // For the view first (lectern-view.ts), then clear of the models.
    const forView = viewPlacement({ eye, yaw, subject, current, eyeHeight, assembly });
    if (!touches(forView))
        return forView;
    // Clear of the subject as the view rule has it: a real overlap moves a
    // centred lectern, a swung one clears by 2°.
    const inView = (placement) => placement.side === 0
        ? !hits(placement, centredFrom(current) ? 0 : FRAMING.releaseMargin)
        : !hits(placement, FRAMING.swingMargin);
    const preferred = span ? awayFrom(span) : awayFromTouched();
    let grazing;
    let hiding;
    for (const candidate of candidates(preferred)) {
        if (touches(candidate))
            continue;
        if (inView(candidate))
            return candidate;
        if (!hits(candidate, 0))
            grazing ??= candidate;
        else
            hiding ??= { ...candidate, overlapsAfter: true };
    }
    return grazing ?? hiding ?? { ...forView, collides: true };
    /** Away from the models a centred lectern touches; right when they are straight ahead. */
    function awayFromTouched() {
        const box = lecternBox({ eye, yaw, placement: CENTRED, eyeHeight, assembly });
        const touched = obstacles
            .filter((obstacle) => lecternTouches(box, obstacle))
            .map((obstacle) => (obstacle.ring ? ringNearLectern(obstacle, box) : obstacle));
        const around = unionBounds(touched);
        if (!around)
            return 1;
        return anglesOf(toRig(centreOf(around.obstacle), eye, yaw))[0] > 1 ? -1 : 1;
    }
}
/** Of a ring the lectern touches, where it does: the point of its line nearest the lectern. */
function ringNearLectern(bounds, box) {
    const { centre, inner, outer } = bounds.ring;
    const dx = box.centre[0] - centre[0];
    const dz = box.centre[2] - centre[1];
    const d = Math.hypot(dx, dz) || 1;
    const r = (inner + outer) / 2;
    const y = (bounds.obstacle.min[1] + bounds.obstacle.max[1]) / 2;
    const at = [centre[0] + (dx / d) * r, y, centre[1] + (dz / d) * r];
    const point = { min: at, max: at };
    return { box: point, obstacle: point };
}
/**
 * The places the lectern may stand, nearest to where it belongs first:
 * centred, then a degree further to either side (`preferred` first) up to
 * 45°, on each of its distances in turn.
 */
function* candidates(preferred) {
    for (const distance of LECTERN.distances) {
        const nearer = distance === LECTERN.distance ? {} : { distance };
        yield { ...CENTRED, ...nearer };
        for (let degrees = 1; degrees <= FRAMING.maxSwing; degrees++) {
            for (const side of [preferred, -preferred]) {
                yield { side, degrees, overlapsAfter: false, ...nearer };
            }
        }
    }
}
/** Sutherland–Hodgman: `polygon` clipped to the convex `window`, anticlockwise. */
function clip(polygon, window) {
    let out = [...polygon];
    for (let i = 0; i < window.length && out.length > 0; i++) {
        const a = window[i];
        const b = window[(i + 1) % window.length];
        const keep = (p) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]) >= 0;
        const input = out;
        out = [];
        for (let j = 0; j < input.length; j++) {
            const p = input[j];
            const q = input[(j + 1) % input.length];
            const pIn = keep(p);
            const qIn = keep(q);
            if (pIn)
                out.push(p);
            if (pIn !== qIn) {
                const dx = q[0] - p[0];
                const dy = q[1] - p[1];
                const ex = b[0] - a[0];
                const ey = b[1] - a[1];
                const t = (ex * (a[1] - p[1]) - ey * (a[0] - p[0])) / (ex * dy - ey * dx);
                out.push([p[0] + dx * t, p[1] + dy * t]);
            }
        }
    }
    return out;
}
const area = (polygon) => {
    let sum = 0;
    for (let i = 0; i < polygon.length; i++) {
        const [x0, y0] = polygon[i];
        const [x1, y1] = polygon[(i + 1) % polygon.length];
        sum += x0 * y1 - x1 * y0;
    }
    return Math.abs(sum) / 2;
};
/**
 * How much of a subject the lectern hides: the share of its projected outline
 * inside the lectern's outline, 0 to 1.
 */
export function panelCover(input) {
    const subject = spanOf(input.subject, input.eye, input.yaw).outline;
    const whole = area(subject);
    if (whole <= 0)
        return 0;
    const outline = panelOutline(input.eyeHeight ?? input.eye[1], input.placement.side, input.placement.degrees);
    // The lectern's outline bows at its top edge, so it is what gets clipped,
    // by the subject's convex outline.
    return Math.min(1, area(clip(outline, subject)) / whole);
}
/** A world's features as subjects: round, far off. */
function featureBounds(lesson, worlds) {
    const bounds = new Map();
    for (const [name, feature] of Object.entries(featuresOf(lesson, worlds))) {
        bounds.set(`wereld:${name}`, roundBounds(feature.round));
    }
    return bounds;
}
/**
 * What framing needs of the scene in `states`; library models only with the
 * `catalogue` that knows them, their footprints swept by `clips` (each
 * model's clips at the moment, as `framingSceneAt` gives them).
 */
export function framingScene(lesson, states = declaredStates(lesson), catalogue, clips, worlds) {
    const satellites = new Map();
    const posters = new Map();
    const models = new Map();
    let light;
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        const state = states.get(id);
        if (!state?.visible)
            continue;
        const slug = modelSlugOf(object.kind);
        const facts = slug === undefined ? undefined : catalogue?.[slug];
        if (facts) {
            const scale = modelScale(object, facts) * state.scale;
            models.set(id, {
                small: isSmallModel(facts, scale),
                exhibit: isStaticModel(facts),
                footprint: modelFootprint(facts, [state.pos[0], state.pos[2]], state.rot[1], {
                    scale,
                    clips: clips?.get(id),
                }),
            });
        }
        // Its facing is its turn about y, which sceneStateAt worked out.
        if (isPosterKind(object.kind))
            posters.set(id, state.rot[1]);
        const [namespace = ''] = object.kind.split(':');
        if (namespace === 'star')
            light ??= state.pos;
        if (object.parent !== undefined && object.orbit !== undefined) {
            const radius = (object.radius ?? DEFAULT_RADIUS[namespace] ?? 0.5) *
                state.scale;
            satellites.set(object.parent, Math.max(satellites.get(object.parent) ?? 0, object.orbit + radius));
        }
    }
    const scene = {
        bounds: lessonBounds(lesson, states, catalogue),
        states,
        satellites,
        posters,
        ...(models.size > 0 ? { models } : {}),
        // A world or a 360° photo: the pupil stays on the stage. A pano has no
        // features (`featuresOf` knows worlds only), so its map is empty.
        ...(isStagedLesson(lesson, worlds)
            ? (() => {
                const stage = stageOf(lesson, worlds);
                return {
                    standWithin: stage.stand,
                    strictStand: stage.strictStand,
                    modelEyes: stage.modelEyes,
                    features: featureBounds(lesson, worlds),
                };
            })()
            : {}),
    };
    return light ? { ...scene, light } : scene;
}
/**
 * Every clip each library model plays during step `index` (the calm path):
 * what it is in as the step starts, and the gait, play and end of its entry.
 */
export function modelClipsAt(lesson, index, catalogue) {
    return modelStepAt(lesson, index, catalogue).clips;
}
/**
 * Each library model's clips during step `index` (`modelClipsAt`), and the
 * circle it goes round in the step (ADR-054): its own entry's, or the loop it
 * carries on from an earlier step that this one does not name.
 */
function modelStepAt(lesson, index, catalogue) {
    const clips = new Map();
    const circles = new Map();
    const turned = new Map();
    const replay = replayModels(lesson, { catalogue, until: index, pupil: () => undefined });
    const before = modelPosesAt(replay, index - 1);
    for (const [id, pose] of before) {
        const move = index >= 0 ? replay.steps[index]?.get(id) : undefined;
        clips.set(id, move ? [...new Set([pose.animation, ...move.clips])] : [pose.animation]);
        const circle = move ? move.circle : index >= 0 ? pose.circle : undefined;
        if (circle)
            circles.set(id, circle);
        if (move?.turned)
            turned.set(id, move.turned);
    }
    return { clips, circles, turned };
}
/**
 * A footprint as the model ends its turn in place, swept back through the whole turn about `pos`: the
 * convex hull of its corners every degree of the way, each pushed 1 cm further out -- more than a corner
 * bulges between two degrees. What framing keeps the eyes from, as the distance rule sweeps the turn.
 */
function turnSwept(footprint, pos, turn) {
    const delta = shortestTurn(turn.from, turn.to);
    const n = Math.max(1, Math.ceil(Math.abs(delta)));
    const corners = [];
    for (let k = 0; k <= n; k++) {
        // Back from where it ends (`to`) towards where it began.
        const radians = (-delta * (1 - k / n) * Math.PI) / 180;
        const c = Math.cos(radians);
        const s = Math.sin(radians);
        for (const [x, z] of footprint) {
            const [dx, dz] = [x - pos[0], z - pos[1]];
            const [rx, rz] = [dx * c + dz * s, -dx * s + dz * c];
            const out = Math.hypot(rx, rz);
            const grow = out > 0 ? (out + 0.01) / out : 1;
            corners.push([pos[0] + rx * grow, pos[1] + rz * grow]);
        }
    }
    return convexHull(corners);
}
/** The convex hull of `points`, anticlockwise (Andrew's monotone chain). */
function convexHull(points) {
    const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (sorted.length < 3)
        return sorted;
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const half = (list) => {
        const out = [];
        for (const p of list) {
            while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0)
                out.pop();
            out.push(p);
        }
        out.pop();
        return out;
    };
    return [...half(sorted), ...half([...sorted].reverse())];
}
/**
 * The scene at step `index` (`sceneStateAt`), with what the lectern must keep
 * clear of: each object where the step starts it and where it ends it, so a
 * move cannot carry a model through the lectern on its way. With a
 * `catalogue`, library models too, as the box their step's clips sweep (a
 * bite reaches beyond the snout).
 */
export function framingSceneAt(lesson, index, statesAt = (i) => sceneStateAt(lesson, i), catalogue, worlds) {
    const after = statesAt(index);
    const before = statesAt(index - 1);
    const step = catalogue ? modelStepAt(lesson, index, catalogue) : undefined;
    const clips = step?.clips;
    const framed = framingScene(lesson, after, catalogue, clips, worlds);
    // A model turned in place (plan 4): the eyes keep clear of all of its turn, not only where it ends.
    const models = framed.models && step && step.turned.size > 0
        ? new Map([...framed.models].map(([id, model]) => {
            const turn = step.turned.get(id);
            const state = after.get(id);
            return [
                id,
                turn && state
                    ? {
                        ...model,
                        footprint: turnSwept(model.footprint, [state.pos[0], state.pos[2]], turn),
                    }
                    : model,
            ];
        }))
        : undefined;
    const scene = models ? { ...framed, models } : framed;
    const obstacles = new Map();
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        const playing = clips?.get(id);
        const solid = (states) => {
            const state = states.get(id);
            const bounds = state ? objectBounds(object, state, catalogue, playing) : undefined;
            const solids = state && bounds ? objectSolids(object, state, catalogue, playing) : undefined;
            return bounds && solids ? { ...bounds, solids } : bounds;
        };
        const [start, end] = [solid(before), solid(after)];
        const [from, to] = [before.get(id), after.get(id)];
        const circle = step?.circles.get(id);
        const over = circle && start && end && from && to
            ? ringSwept(circle, [start, from], [end, to])
            : start && end && from && to
                ? sweep(start, end, from, to)
                : (start ?? end);
        if (over)
            obstacles.set(id, over);
    }
    return { ...scene, obstacles };
}
/**
 * Where a model is over a step that may move it: as it is, or the box around
 * both ends -- and, when it turns, around everything it can reach while it
 * turns, which a half turn that ends where it began does not show.
 */
function sweep(before, after, from, to) {
    const same = (a, b) => [0, 1, 2].every((i) => a.min[i] === b.min[i] && a.max[i] === b.max[i]);
    const turns = [0, 1, 2].some((i) => from.rot[i] !== to.rot[i]);
    if (!turns && same(before.obstacle, after.obstacle) && same(before.box, after.box))
        return after;
    const ends = [
        { box: before.box, obstacle: before.obstacle },
        { box: after.box, obstacle: after.obstacle },
    ];
    const discs = [];
    if (turns) {
        // Turned about the room's y axis only (the headset turns it the short way
        // round, a quaternion): a cylinder about its pivot, at its own height.
        // Any other turn: the ball about its pivot that its corners reach.
        const upright = from.rot[0] === 0 && to.rot[0] === 0 && from.rot[2] === to.rot[2];
        for (const [bounds, state] of [
            [before, from],
            [after, to],
        ]) {
            const corners = boxCorners(bounds.obstacle).map((c) => sub(c, state.pos));
            const [x, y, z] = state.pos;
            const reach = Math.max(...corners.map((c) => (upright ? Math.hypot(c[0], c[2]) : length(c))));
            const low = upright ? bounds.obstacle.min[1] : y - reach;
            const high = upright ? bounds.obstacle.max[1] : y + reach;
            const around = { min: [x - reach, low, z - reach], max: [x + reach, high, z + reach] };
            ends.push({ box: around, obstacle: around });
            discs.push({ centre: [x, z], radius: reach });
        }
    }
    // A box around all of it, not the round thing either end is: it is somewhere between. With the
    // parts of its hull, which the stage measures by (ADR-102): the box's corners lie beyond the move.
    return {
        ...unionBounds(ends),
        swept: { ends: [before.obstacle, after.obstacle], discs },
    };
}
/**
 * Where a model that goes round is over the step (ADR-054): the ring its
 * footprint sweeps -- as far either side of the circle as any corner of its
 * box (clips and all) reaches from its pivot, turned any way -- at the heights
 * it has at either end, `above` included. The whole ring, even for part of a
 * round: the distance rule judges a round or more the same way, and a ring
 * keeps outside the apron (spec, "The lectern"). Never the box around its two
 * ends, which takes in the middle of the ring where the pupil and the lectern are.
 */
function ringSwept(circle, ...ends) {
    let half = 0;
    let low = Infinity;
    let high = -Infinity;
    for (const [bounds, state] of ends) {
        for (const corner of boxCorners(bounds.obstacle)) {
            half = Math.max(half, Math.hypot(corner[0] - state.pos[0], corner[2] - state.pos[2]));
        }
        low = Math.min(low, bounds.obstacle.min[1]);
        high = Math.max(high, bounds.obstacle.max[1]);
    }
    const [x, z] = circle.centre;
    const outer = circle.radius + half;
    const square = { min: [x - outer, low, z - outer], max: [x + outer, high, z + outer] };
    return {
        box: square,
        obstacle: square,
        ring: { centre: [x, z], inner: Math.max(0, circle.radius - half), outer },
    };
}
/**
 * What a step is about: its `view.look` object and its `focus`, in one box.
 * A step that looks at a point with no focus, or at a `{station}`, has none.
 */
export function subjectOf(step, scene) {
    const view = step.view;
    if (view && 'station' in view)
        return undefined;
    const look = view && 'look' in view && typeof view.look === 'string' ? view.look : undefined;
    // A far feature joins the subject only when the step looks at it: with an
    // object in view, its 120 m of bounds would send the pupil off the stage.
    const lookingAtObject = look !== undefined && featureOf(look) === undefined;
    const known = (id) => scene.bounds.get(id) ?? (lookingAtObject ? undefined : scene.features?.get(id));
    const ids = [...new Set([look, step.focus])].filter((id) => id !== undefined && known(id) !== undefined);
    const bounds = unionBounds(ids.map((id) => known(id)));
    return bounds ? { ids, bounds } : undefined;
}
/**
 * A `{look: id}` step, framed: from the lit side (or away from the origin),
 * kept clear of the subject's orbiting children and of every other object.
 * Undefined for any other view, or when the subject is not there.
 */
export function frameStep(step, scene, eye) {
    const view = step.view;
    if (!view || !('look' in view) || 'pos' in view || typeof view.look !== 'string')
        return undefined;
    // A world's feature is faced from where the pupil stands, never walked to;
    // so is a big library model (ADR-043): the pupil turns to it.
    if (featureOf(view.look) !== undefined)
        return undefined;
    if (scene.models?.get(view.look)?.small === false)
        return undefined;
    const subject = subjectOf(step, scene);
    if (!subject)
        return undefined;
    const small = subject.ids.some((id) => scene.models?.get(id)?.small === true);
    const keepAway = [];
    for (const [id, model] of scene.models ?? []) {
        if (!model.small) {
            keepAway.push({
                footprint: model.footprint,
                distance: model.exhibit
                    ? MODEL_DISTANCE.smallEyes
                    : (scene.modelEyes ?? MODEL_DISTANCE.calmEyes),
            });
        }
        else if (subject.ids.includes(id)) {
            keepAway.push({ footprint: model.footprint, distance: MODEL_DISTANCE.smallEyes });
        }
    }
    const centre = centreOfBounds(subject.bounds);
    const from = scene.light ?? [0, 0, 0];
    let dx = centre[0] - from[0];
    let dz = centre[2] - from[2];
    const flat = Math.hypot(dx, dz);
    // A poster is looked at from its front: the pupil comes at it against the
    // way it faces.
    const facing = scene.posters?.get(view.look);
    if (facing !== undefined) {
        const [nx, nz] = posterNormal(facing);
        dx = -nx;
        dz = -nz;
    }
    else if (flat < 1e-6) {
        // The subject is the light, or right above it: any way will do, and +z is
        // what the stand-off always used.
        dx = 0;
        dz = 1;
    }
    else {
        dx /= flat;
        dz /= flat;
    }
    const reach = Math.max(0, ...subject.ids.map((id) => scene.satellites.get(id) ?? 0));
    const obstacles = [...scene.bounds].filter(([id]) => !subject.ids.includes(id)).map(([, b]) => b);
    return frameView({
        subject: subject.bounds,
        obstacles,
        approach: [dx, dz],
        eye,
        // A question's lectern stands higher: framed above that one.
        assembly: lecternAssembly(step),
        ...(reach > 0 ? { keepOut: reach + FRAMING.satellite } : {}),
        ...(scene.standWithin !== undefined ? { standWithin: scene.standWithin } : {}),
        ...(scene.strictStand === true ? { strictStand: true } : {}),
        ...(small ? { surface: MODEL_DISTANCE.smallEyes + MODEL_DISTANCE.margin } : {}),
        ...(keepAway.length > 0 ? { keepAway } : {}),
    });
}
/**
 * Where a `{station}` view stands: the stand-off the engine has always used,
 * unchanged (the campus tour is its only user), on the lit side.
 */
export function stationStandOff(input) {
    const radius = input.radius ?? 0.5;
    // Rings are much wider than the body, so they need more room.
    const spread = input.ringOuter ?? radius;
    const distance = Math.max(radius, spread) * 2.4 + 1.6;
    const from = input.light ?? [0, 0, 0];
    let dx = input.position[0] - from[0];
    let dz = input.position[2] - from[2];
    const flat = Math.hypot(dx, dz);
    if (flat < 1e-6) {
        dx = 0;
        dz = 1;
    }
    else {
        dx /= flat;
        dz /= flat;
    }
    return [input.position[0] - dx * distance, input.eye, input.position[2] - dz * distance];
}
const dutchMetres = (n) => n.toFixed(1).replace('.', ',');
/**
 * Walks a lesson's steps as the headset does -- the pupil starts at the
 * origin facing −z, a step with a `view` moves them, a step without one leaves
 * them where they were -- and says what is wrong with each step's view of its
 * subject. The lectern follows the eyes, so its answers are the headset's at
 * any eye height from 1.40 m up.
 *
 * Each step is judged as it ends -- after its shows, hides and moves -- so the
 * pupil stands where the result can be seen (`sceneStateAt`, unless the
 * caller gives `statesAt`).
 *
 * With a `catalogue`, library models are seen too (ADR-043): where the steps
 * walked and turned them (`modelStatesAt`, unless `statesAt` is given), a
 * big one faced from where the pupil stands, never walked up to.
 */
export function assessFraming(lesson, eye = 1.6, options = {}, catalogue) {
    const result = { problems: [], collisions: [], notes: [], steps: [] };
    if (lesson.mode !== 'vr')
        return result;
    const statesAt = options.statesAt ??
        (catalogue
            ? modelStatesAt(lesson, catalogue, undefined, options.worlds)
            : (index) => sceneStateAt(lesson, index));
    let at = [0, eye, 0];
    let yaw = 0;
    lesson.steps.forEach((step, index) => {
        const scene = framingSceneAt(lesson, index, statesAt, catalogue, options.worlds);
        const view = step.view;
        let framed;
        let lookAt;
        if (view && 'station' in view) {
            const state = scene.states.get(view.station);
            const bounds = scene.bounds.get(view.station);
            if (state && bounds) {
                at = stationStandOff({
                    position: state.pos,
                    radius: bounds.round?.radius ?? (bounds.box.max[0] - bounds.box.min[0]) / 2,
                    light: scene.light,
                    eye,
                });
                lookAt = state.pos;
            }
        }
        else if (view && 'pos' in view) {
            // The rig stands at pos.y − 1.6, and the pupil's own eyes are above that.
            at = [view.pos[0], view.pos[1] - 1.6 + eye, view.pos[2]];
            lookAt =
                typeof view.look === 'string'
                    ? (scene.states.get(view.look)?.pos ?? scene.features?.get(view.look)?.round?.centre)
                    : view.look;
        }
        else if (view && featureOf(view.look) !== undefined) {
            // A far feature: the pupil stays where they stand, and turns to face it.
            lookAt = scene.features?.get(view.look)?.round?.centre;
        }
        else if (view && scene.models?.get(view.look)?.small === false) {
            // A big library model: likewise, so the pupil is never walked up to it.
            const bounds = scene.bounds.get(view.look);
            lookAt = bounds ? centreOf(bounds.box) : undefined;
        }
        else if (view) {
            framed = frameStep(step, scene, eye);
            if (framed) {
                at = framed.eye;
                lookAt = framed.lookAt;
            }
        }
        if (lookAt)
            yaw = yawTowards(at, lookAt);
        const subject = subjectOf(step, scene);
        const assembly = lecternAssembly(step);
        const obstacles = [...(scene.obstacles ?? scene.bounds).values()];
        // As the headset does: every new step starts from a centred lectern, so a
        // swing never outlives what it was for. A step without a view about the
        // same subject, in the same scene, lands where the last one was.
        const panel = panelPlacement({
            eye: at,
            yaw,
            subject: subject?.bounds,
            current: CENTRED,
            eyeHeight: eye,
            obstacles,
            assembly,
        });
        const lectern = lecternPose(eye, panel.side, panel.degrees, reachOf(panel, assembly));
        if (panel.collides) {
            // Named by the model a centred lectern goes through: where it belongs.
            const box = lecternBox({ eye: at, yaw, placement: CENTRED, eyeHeight: eye, assembly });
            const blocking = [...(scene.obstacles ?? scene.bounds)].find(([, bounds]) => lecternTouches(box, bounds)) ??
                [...(scene.obstacles ?? scene.bounds)].find(([, bounds]) => lecternTouches(lecternBox({ eye: at, yaw, placement: panel, eyeHeight: eye, assembly }), bounds));
            const name = blocking?.[0] ?? '?';
            result.collisions.push({
                step: step.id,
                object: name,
                kind: 'lectern',
                detail: `stap "${step.id}": het tekstpaneel kan nergens staan zonder door "${name}" te gaan; zet de leerling verder van "${name}" of "${name}" verder weg`,
            });
        }
        const record = {
            step: step.id,
            eye: at,
            yaw,
            framed: framed !== undefined,
            panel,
            assembly,
            lectern,
            ...(subject
                ? (() => {
                    const centre = centreOfBounds(subject.bounds);
                    const fill = fillOf(subject.bounds, at);
                    return {
                        subject: subject.ids,
                        distance: Math.hypot(centre[0] - at[0], centre[2] - at[2]),
                        fill: Math.max(fill.tall, fill.wide),
                        cover: panelCover({
                            eye: at,
                            yaw,
                            subject: subject.bounds,
                            placement: panel,
                            eyeHeight: eye,
                        }),
                    };
                })()
                : {}),
        };
        result.steps.push(record);
        if (!subject)
            return;
        const name = subject.ids[0];
        const problem = (object, kind, what) => {
            result.problems.push({
                step: step.id,
                object,
                kind,
                detail: `stap "${step.id}": "${object}" ${what}`,
            });
        };
        if (view && 'pos' in view && lookAt) {
            const ahead = sub(lookAt, at);
            const toSubject = sub(centreOfBounds(subject.bounds), at);
            const cos = dot(ahead, toSubject) / (length(ahead) * length(toSubject) || 1);
            if (Math.acos(Math.max(-1, Math.min(1, cos))) * DEG > FRAMING.behind) {
                problem(name, 'behind', 'staat achter of naast de leerling');
            }
        }
        // A poster seen from behind or edge-on shows nothing (part B).
        for (const id of subject.ids) {
            const facing = scene.posters?.get(id);
            const centre = scene.bounds.get(id);
            if (facing === undefined || !centre)
                continue;
            const middle = centreOfBounds(centre);
            const [nx, nz] = posterNormal(facing);
            const ex = at[0] - middle[0];
            const ez = at[2] - middle[2];
            const cos = (ex * nx + ez * nz) / (Math.hypot(ex, ez) || 1);
            if (Math.acos(Math.max(-1, Math.min(1, cos))) * DEG > POSTER_LIMITS.sideAngle) {
                problem(id, 'side', 'hangt met de achterkant of zijkant naar de leerling');
            }
        }
        let nearest;
        for (const [id, bounds] of scene.bounds) {
            const distance = surfaceDistance(bounds, at);
            if (distance < FRAMING.surfaceFloor && (!nearest || distance < nearest.distance)) {
                nearest = { id, distance };
            }
        }
        if (nearest) {
            problem(nearest.id, 'close', nearest.distance <= 0
                ? 'is te dichtbij (de leerling staat erin)'
                : `is te dichtbij (${dutchMetres(nearest.distance)} m)`);
        }
        else {
            // A world's feature may be bigger than the view (30 Oct 2026, Pieter): a planet, a dust
            // storm or the sky is meant to fill it, and the world's author placed the pupil there.
            // Objects and models beside it must still fit.
            const sized = subject.ids.filter((id) => featureOf(id) === undefined && scene.bounds.has(id));
            const bounds = unionBounds(sized.map((id) => scene.bounds.get(id)));
            const fill = bounds ? fillOf(bounds, at) : undefined;
            if (fill && Math.max(fill.tall, fill.wide) * 2 > FRAMING.tooBig) {
                problem(sized[0], 'big', 'is te groot om te overzien');
            }
        }
        if (framed?.pulledIn) {
            if (scene.strictStand === true && framed.fits === false) {
                problem(name, 'outside', `past niet in beeld vanaf het podium: de leerling zou ${dutchMetres(framed.wanted)} m van het midden moeten staan (hooguit ${String(scene.standWithin).replace('.', ',')}); zet het verder weg of maak het kleiner`);
            }
            else {
                result.notes.push(`stap "${step.id}": "${name}" van ${framed.pushedBack ? 'verder weg' : 'dichterbij'} in beeld, om binnen het podium te blijven`);
            }
        }
        if (panel.overlapsAfter) {
            result.notes.push(panel.degrees === FRAMING.maxSwing && panel.distance === undefined
                ? `stap "${step.id}": "${name}" raakt het paneel nog, ook ${FRAMING.maxSwing}° opzij`
                : `stap "${step.id}": "${name}" raakt het paneel nog, dat daar moet staan om geen model te raken`);
        }
    });
    return result;
}
/**
 * Every step whose lectern has nowhere to stand clear of the models, for any
 * of the pupils in `LECTERN_EYES`: what the generator's "Tekstpaneel vrij van
 * modellen" check fails on. Each step and model named once. A step whose
 * pupil stands in or right next to a model is left out: that is a viewpoint
 * to move ("Onderwerp in beeld"), and the lectern is judged again after.
 */
export function assessLectern(lesson, catalogue, worlds) {
    const seen = new Set();
    const problems = [];
    // With models, where the steps leave them, worked out once for every pupil.
    const statesAt = catalogue ? modelStatesAt(lesson, catalogue, undefined, worlds) : undefined;
    for (const eye of LECTERN_EYES) {
        const assessed = assessFraming(lesson, eye, { ...(statesAt ? { statesAt } : {}), ...(worlds ? { worlds } : {}) }, catalogue);
        const tooClose = new Set(assessed.problems.filter((p) => p.kind === 'close').map((p) => p.step));
        for (const problem of assessed.collisions) {
            const key = `${problem.step}/${problem.object}`;
            if (seen.has(key) || tooClose.has(problem.step))
                continue;
            seen.add(key);
            problems.push(problem);
        }
    }
    return problems;
}
/**
 * Where the lectern stands first, for every pupil in `LECTERN_EYES` and with
 * or without two rows of answers: centred, 1.9 m ahead, 5 cm proud all round.
 * Metres ahead of the eyes (`near` to `far`), wide, and up to `top` above the
 * floor. What the catalogue asks a generated lesson to keep free, so the
 * engine seldom has to move the lectern.
 */
export function lecternZone() {
    let near = Infinity;
    let far = -Infinity;
    let width = 0;
    let top = -Infinity;
    const question = lecternAssembly({
        predict: { question: '', options: ['', '', '', ''] },
    });
    for (const eye of LECTERN_EYES) {
        for (const assembly of [NAVIGATION_ASSEMBLY, question]) {
            const box = lecternBox({ eye: [0, eye, 0], yaw: 0, placement: CENTRED, assembly });
            for (const sx of [-1, 1]) {
                for (const sy of [-1, 1]) {
                    for (const sz of [-1, 1]) {
                        const p = [0, 1, 2].map((k) => box.centre[k] +
                            sx * box.half[0] * box.axes[0][k] +
                            sy * box.half[1] * box.axes[1][k] +
                            sz * box.half[2] * box.axes[2][k]);
                        near = Math.min(near, -p[2]);
                        far = Math.max(far, -p[2]);
                        width = Math.max(width, 2 * Math.abs(p[0]));
                        top = Math.max(top, p[1]);
                    }
                }
            }
        }
    }
    return { near, far, width, top };
}
// --- the pupil a model faces (ADR-043, contract C3) ---------------------------
/**
 * "The pupil" of each step, as models know it (contract C3): the eyes framing
 * places for a pupil of `eye` metres -- a step's `view.pos` or `station`
 * moves them, a `{look}` frames them, a big model is faced from where they
 * stand -- with the models turned as they were before their `face`, so
 * framing and facing do not chase each other. The replay's `face`, the
 * distance rule and the generator's checks all use these.
 */
export function modelPupils(lesson, catalogue, eye = 1.6, worlds) {
    const faceless = (index) => sceneStateAt(lesson, index, { pupil: () => undefined });
    return assessFraming(lesson, eye, { statesAt: faceless, ...(worlds ? { worlds } : {}) }, catalogue).steps.map((s) => s.eye);
}
/**
 * The scene at every step with the models where the steps leave them,
 * facing the pupil `modelPupils` says (a seated child to a tall adult stand
 * close enough that one turn does for all): what framing, the lectern rule
 * and the distance rule judge with a catalogue. Each step worked out once.
 */
export function modelStatesAt(lesson, catalogue, 
/** `modelPupils(lesson, catalogue)` when the caller has it already. */
given, worlds) {
    const hasModels = Object.values(lesson.objects ?? {}).some((object) => modelSlugOf(object.kind) !== undefined);
    if (!hasModels)
        return (index) => sceneStateAt(lesson, index);
    const pupils = given ?? modelPupils(lesson, catalogue, 1.6, worlds);
    const cache = new Map();
    return (index) => {
        let states = cache.get(index);
        if (!states) {
            states = sceneStateAt(lesson, index, {
                pupil: (i) => {
                    const eye = pupils[i];
                    return eye ? [eye[0], eye[2]] : undefined;
                },
            });
            cache.set(index, states);
        }
        return states;
    };
}
//# sourceMappingURL=framing.js.map